/**
 * Tenant-scoped conversion tracking.
 *
 * Some tenants run their own ad campaigns and want a Purchase conversion to
 * fire on THEIR post-purchase moment (membership enrolled) — WITHOUT loading
 * their pixels for every other tenant on the shared platform.
 *
 * This module keeps that isolation: pixels are matched by the request hostname
 * and are lazy-injected + fired only for the matching tenant. No pixel here
 * ever loads on another tenant's site.
 *
 * To add a tenant: add an entry to TENANT_PIXELS keyed by the hostnames their
 * storefront serves from.
 */

// Per-tenant marketing pixel config. Hostname match is case-insensitive and
// also matches the bare apex (e.g. "toptiercoinlaundry.com" matches
// "book.toptiercoinlaundry.com").
const TENANT_PIXELS = [
  {
    // Top Tier Coin Laundry
    hosts: ['toptiercoinlaundry.com', 'book.toptiercoinlaundry.com'],
    ga4: 'G-JM0P68H2BJ',
    googleAds: 'AW-17332637874',
    // Google Ads Purchase conversion LABEL — the string after the "/" in the
    // event snippet, e.g. send_to: 'AW-17332637874/AbC-D_efGh'. Not provided
    // yet; leave null and the Ads conversion is skipped until it's filled in.
    googleAdsPurchaseLabel: null,
    metaPixel: '1117355309834366',
  },
];

// Guard so we inject each vendor's base script at most once per page load.
const _loaded = { ga4: false, googleAds: false, meta: false };

function _hostMatches(host, entryHosts) {
  const h = (host || '').toLowerCase();
  return entryHosts.some((e) => h === e || h.endsWith(`.${e}`) || h === `www.${e}`);
}

function _pixelsForHost(host) {
  return TENANT_PIXELS.find((t) => _hostMatches(host, t.hosts)) || null;
}

function _injectScript(src, async = true) {
  const s = document.createElement('script');
  s.src = src;
  s.async = async;
  document.head.appendChild(s);
  return s;
}

function _ensureGtag() {
  if (window.gtag) return;
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
}

/**
 * Fire a membership-purchase conversion for the tenant that owns `hostname`.
 * No-op for any host without a configured pixel set.
 *
 * @param {Object} opts
 * @param {string} opts.hostname   window.location.hostname (injectable for tests)
 * @param {number} opts.value      purchase value (plan monthly price)
 * @param {string} [opts.currency] ISO currency, default 'USD'
 * @param {string} [opts.planName] plan name (sent as item name where supported)
 * @param {string} [opts.transactionId] optional order/enrollment id for dedup
 */
export function fireMembershipConversion({
  hostname,
  value,
  currency = 'USD',
  planName,
  transactionId,
} = {}) {
  try {
    const host = hostname || (typeof window !== 'undefined' ? window.location.hostname : '');
    const cfg = _pixelsForHost(host);
    if (!cfg) return; // not a tracked tenant — do nothing

    const numValue = Number(value) || 0;

    // ── GA4 (gtag) — separate property from the platform-wide GA. ──────────
    if (cfg.ga4) {
      _ensureGtag();
      if (!_loaded.ga4) {
        _injectScript(`https://www.googletagmanager.com/gtag/js?id=${cfg.ga4}`);
        window.gtag('config', cfg.ga4);
        _loaded.ga4 = true;
      }
      window.gtag('event', 'purchase', {
        send_to: cfg.ga4,
        value: numValue,
        currency,
        transaction_id: transactionId || undefined,
        items: planName ? [{ item_name: planName, price: numValue, quantity: 1 }] : undefined,
      });
    }

    // ── Google Ads conversion (uses gtag too). ─────────────────────────────
    if (cfg.googleAds) {
      _ensureGtag();
      if (!_loaded.googleAds) {
        _injectScript(`https://www.googletagmanager.com/gtag/js?id=${cfg.googleAds}`);
        window.gtag('config', cfg.googleAds);
        _loaded.googleAds = true;
      }
      // Only fire the Ads conversion if the Purchase conversion LABEL is known.
      if (cfg.googleAdsPurchaseLabel) {
        window.gtag('event', 'conversion', {
          send_to: `${cfg.googleAds}/${cfg.googleAdsPurchaseLabel}`,
          value: numValue,
          currency,
          transaction_id: transactionId || undefined,
        });
      }
    }

    // ── Meta Pixel (fbq). ──────────────────────────────────────────────────
    if (cfg.metaPixel) {
      if (!window.fbq) {
        /* eslint-disable */
        (function (f, b, e, v, n, t, s) {
          if (f.fbq) return; n = f.fbq = function () {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
          };
          if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0';
          n.queue = []; t = b.createElement(e); t.async = !0;
          t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
        })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
        /* eslint-enable */
      }
      if (!_loaded.meta) {
        window.fbq('init', cfg.metaPixel);
        _loaded.meta = true;
      }
      window.fbq('track', 'Purchase', { value: numValue, currency });
    }
  } catch (e) {
    // Never let analytics break the purchase flow.
    // eslint-disable-next-line no-console
    console.warn('conversion tracking skipped:', e);
  }
}

export default fireMembershipConversion;
